export interface ExtractedContent {
    cssContent: string;
    jsContent: string;
    htmlFiles: string[];
}

/**
 * Extract CSS and JS content from project code synchronously
 */
export const extractCssAndJsContent = (code: string): ExtractedContent => {
    try {
        const parsedCode = JSON.parse(code);
        let cssContent = '';
        let jsContent = '';
        const htmlFiles: string[] = [];
        const cssFiles: string[] = [];

        Object.entries(parsedCode).forEach(([filePath, fileData]: [string, any]) => {
            const content = typeof fileData === 'string' ? fileData : fileData?.code || '';

            if (filePath.endsWith('.css')) {
                cssContent += `\n/* === ${filePath} === */\n${content}\n`;
                cssFiles.push(filePath);
            } else if (filePath.endsWith('.js')) {
                jsContent += `\n/* === ${filePath} === */\n${content}\n`;
            } else if (filePath.endsWith('.html')) {
                htmlFiles.push(filePath);
            }
        });

        const result = {
            cssContent: cssContent.trim(),
            jsContent: jsContent.trim(),
            htmlFiles
        };

        return result;
    } catch (error) {
        console.error('Error extracting CSS/JS content:', error);
        return {
            cssContent: '',
            jsContent: '',
            htmlFiles: []
        };
    }
};

/**
 * Ensure all CSS files are properly injected into HTML head
 */
export const injectCssIntoHtmlHead = (htmlContent: string, cssContent: string): string => {
    if (!cssContent || !cssContent.trim()) {
        return htmlContent;
    }

    // Count CSS files in the content
    const cssFileCount = (cssContent.match(/\/\* === .*? === \*\//g) || []).length;

    let updatedHtml = htmlContent;

    // Check if HTML has a head tag
    if (updatedHtml.includes('<head>') && updatedHtml.includes('</head>')) {
        // Find existing style tags in the head
        const styleTagRegex = /<style[^>]*>[\s\S]*?<\/style>/g;
        const styleTags = updatedHtml.match(styleTagRegex);

        if (styleTags && styleTags.length > 0) {
            // Find the last style tag and append CSS to it
            const lastStyleTag = styleTags[styleTags.length - 1];
            const lastStyleTagIndex = updatedHtml.lastIndexOf(lastStyleTag);

            if (lastStyleTagIndex !== -1) {
                // Extract the content of the last style tag
                const styleContentMatch = lastStyleTag.match(/<style[^>]*>([\s\S]*?)<\/style>/);
                if (styleContentMatch) {
                    const existingStyleContent = styleContentMatch[1];
                    const newStyleContent = `${existingStyleContent}\n\n/* === PROJECT CSS FILES (${cssFileCount} files) === */\n${cssContent}\n/* === END PROJECT CSS === */`;

                    // Replace the last style tag with updated content
                    const newStyleTag = lastStyleTag.replace(styleContentMatch[0], `<style>${newStyleContent}</style>`);
                    updatedHtml = updatedHtml.substring(0, lastStyleTagIndex) + newStyleTag + updatedHtml.substring(lastStyleTagIndex + lastStyleTag.length);

                }
            }
        } else {
            // No style tags found, create a new one before closing head tag
            const styleTag = `<style>
/* === PROJECT CSS FILES (${cssFileCount} files) === */
${cssContent}
/* === END PROJECT CSS === */
</style>`;

            updatedHtml = updatedHtml.replace('</head>', `${styleTag}\n</head>`);
        }
    } else if (updatedHtml.includes('<html>') && updatedHtml.includes('</html>')) {
        // If no head tag, create one and inject CSS
        const styleTag = `<style>
/* === PROJECT CSS FILES (${cssFileCount} files) === */
${cssContent}
/* === END PROJECT CSS === */
</style>`;

        updatedHtml = updatedHtml.replace('<html>', `<html>\n<head>\n${styleTag}\n</head>`);
    } else {
        // If no HTML structure, wrap content and add head with CSS
        const styleTag = `<style>
/* === PROJECT CSS FILES (${cssFileCount} files) === */
${cssContent}
/* === END PROJECT CSS === */
</style>`;

        updatedHtml = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Page</title>
${styleTag}
</head>
<body>
${updatedHtml}
</body>
</html>`;
    }

    return updatedHtml;
};

